import React, { useCallback, useState } from 'react';
import { GridToolbarContainer } from '@mui/x-data-grid';
import {
  Badge,
  Box,
  Button,
  IconButton,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import { MultiFilterBuilder } from './MultiFilterBuilder';
import { GridFilterModel } from '@mui/x-data-grid/models';

interface MuiFilterToolbarProps {
  columns: { field: string; headerName: string }[];
  filterModelRef: React.MutableRefObject<GridFilterModel>;
  onFilterModelChange: (filterModel: GridFilterModel) => void;
  /** Called whenever the user types in the quick search box. */
  onQuickSearchChange: (value: string) => void;
  /** Optional custom component to render in the toolbar */
  customComponent?: React.ReactNode;
}

/**
 * Renders a toolbar with a quick search field + an "Advanced Filters" popover button.
 */
const MuiFilterToolbar: React.FC<MuiFilterToolbarProps> = ({
  columns,
  filterModelRef,
  onFilterModelChange,
  onQuickSearchChange,
  customComponent,
}) => {
  const [quickSearchValue, setQuickSearchValue] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(prev => (prev ? null : event.currentTarget));
  };

  const handleClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);

  /**
   * Handle user typing in the quick search box.
   * We'll push changes up to the parent, but do nothing else locally.
   */
  const handleQuickSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setQuickSearchValue(newValue);
      onQuickSearchChange(newValue);
    },
    [onQuickSearchChange]
  );

  /** Clear the quick search text. */
  const handleClearSearch = () => {
    setQuickSearchValue('');
    onQuickSearchChange('');
  };

  const [filterCount, setFilterCount] = useState(0);

  /**
   * When the filter model changes, update the filter count badge
   */
  const handleFilterModelChange = useCallback(
    (filterModel: GridFilterModel) => {
      onFilterModelChange(filterModel);
      setFilterCount(filterModel.items.length);
    },
    [onFilterModelChange]
  );

  return (
    <Box mb={0.5}>
      <GridToolbarContainer>
        {/* Filter button + badge using MUI Button with startIcon to match MUI grid styling */}
        <Button
          variant="text"
          color="primary"
          size="small"
          aria-label="Show filters"
          aria-haspopup="true"
          aria-expanded={open ? 'true' : 'false'}
          onClick={handleButtonClick}
          startIcon={
            <Badge
              color="primary"
              badgeContent={!quickSearchValue ? filterCount : undefined}
              overlap="rectangular"
            >
              <FilterListIcon />
            </Badge>
          }
        >
          Filters
        </Button>

        {/* Popover for advanced filters */}
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: {
              sx: {
                maxWidth: 600,
                p: 2,
                pb: 1,
              },
            },
          }}
          aria-labelledby="filter-popover-title"
        >
          <Typography id="filter-popover-title" variant="h6" gutterBottom>
            {quickSearchValue ? (
              <>
                Quick search is active,&nbsp;
                <a href="#" onClick={handleClearSearch}>
                  clear
                </a>
                &nbsp;to use filters
              </>
            ) : (
              'Advanced Filters'
            )}
          </Typography>

          {/* Only show MultiFilterBuilder if quick search is empty */}
          {!quickSearchValue && (
            <Box sx={{ minWidth: 300, p: 1 }}>
              <MultiFilterBuilder
                columns={columns}
                filterModelRef={filterModelRef}
                onFilterModelChange={handleFilterModelChange}
              />
            </Box>
          )}
        </Popover>

        {/* Custom component (if provided) */}
        {customComponent && <Box sx={{ ml: 1 }}>{customComponent}</Box>}

        {/* Grow space between filter button and quick search */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Quick Search Field */}
        <TextField
          variant="standard"
          placeholder="Quick search"
          size="small"
          sx={{ width: 300 }}
          value={quickSearchValue}
          onChange={handleQuickSearchChange}
          slotProps={{
            htmlInput: { 'aria-label': 'Quick search' },
            input: {
              endAdornment: quickSearchValue && (
                <IconButton
                  aria-label="clear search"
                  onClick={handleClearSearch}
                  edge="end"
                  size="small"
                >
                  <ClearIcon />
                </IconButton>
              ),
            },
          }}
        />
      </GridToolbarContainer>
    </Box>
  );
};

export default React.memo(MuiFilterToolbar);
